import React, { useState, useEffect } from 'react';
import { CheckCircle, Circle, ChevronDown, ChevronUp, X, Sparkles, Database, Users, GraduationCap, Tag, Users2, Link2, ClipboardCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { AppSettings, Tool, Employee, Curriculum, GovernanceTask } from '../types';

interface ActivationChecklistProps {
  tools: Tool[];
  employees: Employee[];
  curricula: Curriculum[];
  governanceTasks: GovernanceTask[];
  settings: AppSettings;
  onUpdateSettings: (settings: AppSettings) => Promise<void>;
}

interface ChecklistItem {
  key: keyof NonNullable<AppSettings['activationChecklist']>['items'];
  label: string;
  icon: React.ReactNode;
  route: string;
  completed: boolean;
}

export const ActivationChecklist: React.FC<ActivationChecklistProps> = ({
  tools,
  employees,
  curricula,
  governanceTasks,
  settings,
  onUpdateSettings,
}) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [celebrating, setCelebrating] = useState(false);

  const checklist = settings.activationChecklist;
  const hidden = !settings.onboardingCompleted || checklist?.dismissed;

  const computed: Record<string, boolean> = {
    add_first_tool: (tools ?? []).length >= 1,
    add_first_employee: (employees ?? []).length >= 1,
    create_first_training: (curricula ?? []).length >= 1,
    configure_categories: (settings.categories ?? []).length >= 1,
    create_first_team: (settings.teams ?? []).length >= 1,
    assign_tool_to_employee: (tools ?? []).some(t => (t.assignedUserIds ?? []).length >= 1),
    complete_first_governance_task: (governanceTasks ?? []).some(t => t.isCompleted),
  };

  const items: ChecklistItem[] = [
    { key: 'add_first_tool', label: 'Add your first tool', icon: <Database size={16} />, route: '/admin/inventory', completed: computed.add_first_tool },
    { key: 'add_first_employee', label: 'Add a team member', icon: <Users size={16} />, route: '/admin/employees', completed: computed.add_first_employee },
    { key: 'create_first_training', label: 'Create a training curriculum', icon: <GraduationCap size={16} />, route: '/admin/training', completed: computed.create_first_training },
    { key: 'configure_categories', label: 'Set up your categories', icon: <Tag size={16} />, route: '/admin/settings', completed: computed.configure_categories },
    { key: 'create_first_team', label: 'Define a team', icon: <Users2 size={16} />, route: '/admin/settings', completed: computed.create_first_team },
    { key: 'assign_tool_to_employee', label: 'Assign a tool to someone', icon: <Link2 size={16} />, route: '/admin/inventory', completed: computed.assign_tool_to_employee },
    { key: 'complete_first_governance_task', label: 'Complete a governance task', icon: <ClipboardCheck size={16} />, route: '/admin/governance', completed: computed.complete_first_governance_task },
  ];

  const completedCount = items.filter(i => i.completed).length;
  const allDone = completedCount === items.length;
  const progress = (completedCount / items.length) * 100;

  useEffect(() => {
    if (hidden) return;
    const storedItems = checklist?.items;
    if (!storedItems) return;

    const needsUpdate = Object.entries(computed).some(
      ([key, val]) => val && !storedItems[key as keyof typeof storedItems]
    );

    if (needsUpdate) {
      const merged = { ...storedItems };
      for (const [key, val] of Object.entries(computed)) {
        if (val) (merged as any)[key] = true;
      }
      onUpdateSettings({
        ...settings,
        activationChecklist: { ...checklist!, items: merged as typeof storedItems },
      });
    }
  }, [JSON.stringify(computed), hidden]);

  useEffect(() => {
    if (hidden) return;
    if (allDone && !celebrating) {
      setCelebrating(true);
      const timer = setTimeout(() => handleDismiss(), 8000);
      return () => clearTimeout(timer);
    }
  }, [allDone, hidden]);

  if (hidden) return null;

  const handleDismiss = async () => {
    const current = settings.activationChecklist ?? {
      dismissed: false,
      items: {
        add_first_tool: false, add_first_employee: false, create_first_training: false,
        configure_categories: false, create_first_team: false, assign_tool_to_employee: false,
        complete_first_governance_task: false,
      },
    };
    await onUpdateSettings({
      ...settings,
      activationChecklist: { ...current, dismissed: true },
    });
  };

  if (celebrating) {
    return (
      <div className="mb-6 rounded-xl border border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50 p-6 shadow-sm animate-in fade-in">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-emerald-100 p-2 rounded-full">
              <Sparkles className="text-emerald-600" size={24} />
            </div>
            <div>
              <h3 className="font-semibold text-emerald-800 text-lg">Setup complete!</h3>
              <p className="text-emerald-600 text-sm">You've completed all setup steps. Oasix is ready to go.</p>
            </div>
          </div>
          <button onClick={handleDismiss} className="text-emerald-400 hover:text-emerald-600 transition-colors">
            <X size={20} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-6 rounded-xl border border-indigo-100 bg-white shadow-sm">
      <div
        role="button"
        tabIndex={0}
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setIsOpen(!isOpen); } }}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-slate-50 transition-colors rounded-xl cursor-pointer"
      >
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <div className="bg-indigo-100 p-1.5 rounded-lg">
              <Sparkles className="text-indigo-600" size={18} />
            </div>
            <span className="font-semibold text-slate-800">Getting started</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-32 h-2 bg-slate-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-indigo-500 rounded-full transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>
            <span className="text-sm font-medium text-slate-500">{completedCount}/{items.length}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); handleDismiss(); }}
            className="text-slate-400 hover:text-slate-600 p-1 rounded transition-colors"
            title="Dismiss checklist"
          >
            <X size={16} />
          </button>
          {isOpen ? <ChevronUp size={18} className="text-slate-400" /> : <ChevronDown size={18} className="text-slate-400" />}
        </div>
      </div>

      {isOpen && (
        <div className="px-5 pb-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
          {items.map((item) => (
            <button
              key={item.key}
              onClick={() => !item.completed && navigate(item.route)}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-left text-sm transition-all ${
                item.completed
                  ? 'text-slate-400 bg-slate-50 cursor-default'
                  : 'text-slate-700 hover:bg-indigo-50 hover:text-indigo-700 cursor-pointer'
              }`}
            >
              {item.completed ? (
                <CheckCircle size={18} className="text-emerald-500 flex-shrink-0" />
              ) : (
                <Circle size={18} className="text-slate-300 flex-shrink-0" />
              )}
              <span className={`flex items-center gap-2 ${item.completed ? 'line-through' : 'font-medium'}`}>
                <span className={item.completed ? 'text-slate-400' : 'text-indigo-500'}>{item.icon}</span>
                {item.label}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
